import { ApiClient, ApiError, AuthMethodResponse } from '../lib/ApiClient.js';
import { DomBinder } from '../lib/DomBinder.js';
import { Auth } from '../lib/Auth.js';
import { WebAuthn, WebAuthnError } from '../lib/WebAuthn.js';
import { t } from '../lib/i18n.js';

/**
 * Login page controller
 * Handles both magic link and WebAuthn authentication flows
 */
class LoginPage extends DomBinder {
    private apiClient: ApiClient;
    private requestId: string | null = null;
    private email: string | null = null;
    private pollingInterval: number | null = null;
    private authMethod: 'magic_link' | 'webauthn' | null = null;
    private conditionalAbortController: AbortController | null = null;
    private codeSectionShown = false;

    constructor() {
        super();
        this.apiClient = new ApiClient();
        this.init();
    }

    private init(): void {
        // Check for session hint (Safari tab restore workaround)
        // If user was previously logged in, attempt to restore session
        this.attemptSessionRestore();

        // Pre-fill email from query parameter if present
        const urlParams = new URLSearchParams(window.location.search);
        const emailParam = urlParams.get('email');
        if (emailParam) {
            const emailInput = this.getElement<HTMLInputElement>('login-email');
            if (emailInput) {
                emailInput.value = emailParam;
            }
        }

        // Handle form submission
        this.on('login-form', 'submit', (e) => {
            e.preventDefault();
            this.handleSubmit();
        });

        // Handle WebAuthn sign-in button
        this.on('login-webauthn-submit', 'click', () => {
            this.handleWebAuthnAuthentication();
        });

        // Handle cancel button
        this.on('login-cancel', 'click', () => {
            this.resetForm();
        });

        // Handle recovery link
        document.querySelectorAll('.login-recovery-trigger').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleRecoveryRequest();
            });
        });

        // Handle code verification
        this.on('login-code-submit', 'click', () => {
            this.handleCodeVerification();
        });

        // Setup code digit inputs
        this.setupCodeInputs();

        // Start conditional UI authentication (autofill passkeys) in background
        this.startConditionalAuth();
    }

    /**
     * Attempt to restore session using refresh token
     * Workaround for Safari tab restore bug where cookies aren't sent on first request
     */
    private async attemptSessionRestore(): Promise<void> {
        if (!Auth.hasSessionHint()) {
            // No session hint - show login form immediately
            this.showLoginCard();
            return;
        }

        console.log('[Login] Session hint found, attempting restore');

        try {
            const token = await Auth.getValidAccessToken();
            if (token) {
                console.log('[Login] Session restored, redirecting');
                window.location.href = this.getRedirectUrl();
                return;
            }
        } catch (error) {
            console.log('[Login] Session restore failed:', error);
        }

        // Refresh failed - clear hint to prevent loop and show login form
        Auth.clearSessionHint();
        this.showLoginCard();
        console.log('[Login] Session hint cleared');
    }

    /**
     * Show the login page
     */
    private showLoginCard(): void {
        document.body.classList.remove('login-restoring');
    }

    /**
     * Get the URL to redirect to after successful login
     */
    private getRedirectUrl(): string {
        const urlParams = new URLSearchParams(window.location.search);
        const redirect = urlParams.get('redirect');
        // Only allow safe relative paths to prevent open redirect
        // Must start with / but not // (protocol-relative URL)
        if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
            return redirect;
        }
        return '/dashboard';
    }

    /**
     * Start conditional UI authentication in the background
     * This allows passkeys to appear in the browser's autofill dropdown
     */
    private async startConditionalAuth(): Promise<void> {
        // Check if conditional mediation is supported
        const isAvailable = await WebAuthn.isConditionalMediationAvailable();
        if (!isAvailable) {
            console.log('[Login] Conditional UI not available');
            return;
        }

        console.log('[Login] Starting conditional UI authentication');

        try {
            // Get conditional auth options from server
            const options = await this.apiClient.getWebAuthnConditionalOptions();

            // Create abort controller
            this.conditionalAbortController = new AbortController();

            // Start conditional auth (this will wait until user selects a passkey from autofill)
            const credential = await WebAuthn.authenticateConditional(
                options as any,
                this.conditionalAbortController.signal
            );

            console.log('[Login] Passkey selected from autofill');

            // User selected a passkey - complete authentication
            await this.completeConditionalAuth(credential);

        } catch (error) {
            if (error instanceof WebAuthnError && error.code === 'ABORTED') {
                // User proceeded with normal form flow - this is expected
                console.log('[Login] Conditional UI aborted');
                return;
            }

            // Log other errors but don't show to user - conditional UI is optional
            console.error('[Login] Conditional UI error:', error);
        }
    }

    /**
     * Complete authentication after user selects passkey from autofill
     */
    private async completeConditionalAuth(credential: any): Promise<void> {
        this.hideError();
        this.disable('login-submit');
        this.disable('login-email');
        this.setText('login-submit-text', t('auth.signing_in'));

        try {
            // Send credential to server for verification
            const authResponse = await this.apiClient.authenticateWebAuthn(credential);

            // Store access token
            this.storeAccessToken(authResponse.access_token);

            // Show session persistence prompt
            this.showSessionPrompt();

        } catch (error) {
            if (error instanceof ApiError) {
                this.showError(error.message);
            } else {
                this.showError(t('auth.authentication_failed'));
            }

            // Re-enable form
            this.enable('login-submit');
            this.enable('login-email');
            this.setText('login-submit-text', t('auth.continue'));

            // Restart conditional auth
            this.startConditionalAuth();
        }
    }

    /**
     * Abort conditional UI authentication
     */
    private abortConditionalAuth(): void {
        if (this.conditionalAbortController) {
            this.conditionalAbortController.abort();
            this.conditionalAbortController = null;
        }
    }

    private setupCodeInputs(): void {
        const inputs = document.querySelectorAll<HTMLInputElement>('.code-digit');

        inputs.forEach((input, index) => {
            // Handle input
            input.addEventListener('input', (e) => {
                const target = e.target as HTMLInputElement;
                const value = target.value;

                // Only allow digits
                if (!/^\d*$/.test(value)) {
                    target.value = '';
                    return;
                }

                // Auto-advance to next field
                if (value.length === 1 && index < inputs.length - 1) {
                    inputs[index + 1].focus();
                }
            });

            // Handle keydown (backspace and enter)
            input.addEventListener('keydown', (e) => {
                const target = e.target as HTMLInputElement;

                // Handle backspace
                if (e.key === 'Backspace' && !target.value && index > 0) {
                    inputs[index - 1].focus();
                }

                // Handle Enter key to submit
                if (e.key === 'Enter' && this.isCodeComplete()) {
                    this.handleCodeVerification();
                }
            });

            // Handle paste
            input.addEventListener('paste', (e) => {
                e.preventDefault();
                const pastedData = e.clipboardData?.getData('text');

                if (!pastedData) return;

                // Extract digits from pasted data
                const digits = pastedData.replace(/\D/g, '').slice(0, 6);

                // Fill in the digits
                digits.split('').forEach((digit, i) => {
                    if (inputs[i]) {
                        inputs[i].value = digit;
                    }
                });

                // Focus the next empty field or the last field
                const nextEmptyIndex = digits.length < 6 ? digits.length : 5;
                inputs[nextEmptyIndex].focus();
            });
        });
    }

    private getCodeValue(): string {
        const inputs = document.querySelectorAll<HTMLInputElement>('.code-digit');
        return Array.from(inputs).map(input => input.value).join('');
    }

    private isCodeComplete(): boolean {
        const code = this.getCodeValue();
        return code.length === 6 && /^\d{6}$/.test(code);
    }

    private clearCodeInputs(): void {
        const inputs = document.querySelectorAll<HTMLInputElement>('.code-digit');
        inputs.forEach(input => {
            input.value = '';
        });
        if (inputs.length > 0) {
            inputs[0].focus();
        }
    }

    private resetForm(): void {
        this.hide('login-webauthn');
        this.hide('login-polling');
        this.show('login-form');
        this.hideError();
        this.hideSuccess();
        this.enable('login-submit');
        this.enable('login-email');
        this.setText('login-submit-text', t('auth.continue'));
        this.authMethod = null;
        this.codeSectionShown = false;
        this.stopPolling();

        // Clear recovery links
        document.querySelectorAll('.login-recovery-link').forEach(link => {
            link.classList.add('hidden');
        });
    }

    private async handleSubmit(): Promise<void> {
        // Abort any running conditional UI auth
        this.abortConditionalAuth();

        const emailInput = this.getElement<HTMLInputElement>('login-email');
        if (!emailInput || !emailInput.value) {
            this.showError(t('auth.please_enter_email'));
            return;
        }

        const email = emailInput.value.trim();

        // Validate email
        if (!this.isValidEmail(email)) {
            this.showError(t('auth.please_enter_valid_email'));
            return;
        }

        // Clear previous errors
        this.hideError();
        this.hideSuccess();

        // Disable form
        this.disable('login-submit');
        this.disable('login-email');
        this.setText('login-submit-text', t('auth.checking'));

        try {
            // Check which auth method to use
            const authMethodResponse = await this.apiClient.checkAuthMethod(email);
            this.email = email;
            this.authMethod = authMethodResponse.auth_method;

            // Show recovery link if available
            if (authMethodResponse.recovery_available) {
                document.querySelectorAll('.login-recovery-link').forEach(link => {
                    link.classList.remove('hidden');
                });
            }

            if (authMethodResponse.auth_method === 'webauthn') {
                // Show WebAuthn UI
                this.setText('login-security-key-message', t('auth.use_security_key_to_sign_in', { email }));
                this.hide('login-form');
                this.show('login-webauthn');

                // Auto-trigger WebAuthn if supported
                if (WebAuthn.isSupported()) {
                    // Give UI time to render before triggering
                    setTimeout(() => {
                        this.handleWebAuthnAuthentication();
                    }, 100);
                } else {
                    this.showError(t('auth.webauthn_not_supported'));
                }
            } else {
                // Proceed with magic link flow
                await this.sendMagicLink(email);
            }

        } catch (error) {
            if (error instanceof ApiError) {
                this.showError(error.message);
            } else {
                this.showError(t('auth.unexpected_error'));
            }

            // Re-enable form
            this.enable('login-submit');
            this.enable('login-email');
            this.setText('login-submit-text', t('auth.continue'));
        }
    }

    private async sendMagicLink(email: string): Promise<void> {
        try {
            const response = await this.apiClient.requestMagicLink(email);
            this.requestId = response.request_id;

            // Update UI to show polling state
            this.setText('login-sign-in-link-sent', t('auth.sign_in_link_sent', { email }));

            // Update "Try again" link with email parameter
            const tryAgainLink = this.getElement<HTMLAnchorElement>('login-try-again');
            if (tryAgainLink) {
                tryAgainLink.href = `/auth/login?email=${encodeURIComponent(email)}`;
            }

            this.hide('login-form');
            this.show('login-polling');

            // Start polling
            this.startPolling();

        } catch (error) {
            if (error instanceof ApiError) {
                this.showError(error.message);
            } else {
                this.showError(t('auth.failed_send_link'));
            }

            // Re-enable form
            this.enable('login-submit');
            this.enable('login-email');
            this.setText('login-submit-text', t('auth.continue'));
        }
    }

    private async handleWebAuthnAuthentication(): Promise<void> {
        if (!this.email) {
            this.showError(t('auth.session_expired'));
            this.resetForm();
            return;
        }

        this.hideError();
        this.disable('login-webauthn-submit');
        this.setText('login-webauthn-text', t('auth.waiting_for_device'));

        try {
            // Get authentication options from server
            const options = await this.apiClient.getWebAuthnAuthenticationOptions(this.email);

            // Trigger WebAuthn authentication
            const credential = await WebAuthn.authenticate(options as any);

            // Send credential to server for verification
            this.setText('login-webauthn-text', t('auth.verifying'));
            const authResponse = await this.apiClient.authenticateWebAuthn(credential);

            // Store access token
            this.storeAccessToken(authResponse.access_token);

            // Show session persistence prompt
            this.hide('login-webauthn');
            this.showSessionPrompt();

        } catch (error) {
            if (error instanceof WebAuthnError) {
                this.showError(WebAuthn.getUserFriendlyErrorMessage(error));
            } else if (error instanceof ApiError) {
                this.showError(error.message);
            } else {
                this.showError(t('auth.authentication_failed'));
            }

            this.enable('login-webauthn-submit');
            this.setText('login-webauthn-text', t('auth.sign_in_with_security_key'));
        }
    }

    private async handleCodeVerification(): Promise<void> {
        const code = this.getCodeValue();

        if (!code || code.length !== 6) {
            this.showError(t('auth.please_enter_6_digit_code'));
            return;
        }

        if (!this.email) {
            this.showError(t('auth.session_expired_request_new'));
            return;
        }

        // Validate code format
        if (!/^\d{6}$/.test(code)) {
            this.showError(t('auth.please_enter_valid_6_digit_code'));
            return;
        }

        this.hideError();
        this.disable('login-code-submit');
        this.disableCodeInputs();

        try {
            const response = await this.apiClient.verifyMagicLinkCode(this.email, code);

            // Store access token
            this.storeAccessToken(response.access_token);

            // Show session persistence prompt
            this.hide('login-polling');
            this.showSessionPrompt();

        } catch (error) {
            if (error instanceof ApiError) {
                this.showError(error.message);
            } else {
                this.showError(t('auth.failed_verify_code'));
            }

            this.enable('login-code-submit');
            this.enableCodeInputs();
            this.clearCodeInputs();
        }
    }

    private async handleRecoveryRequest(): Promise<void> {
        if (!this.email) {
            this.showError(t('auth.please_enter_email'));
            return;
        }

        // Redirect to recovery page with email pre-filled
        window.location.href = `/auth/recovery?email=${encodeURIComponent(this.email)}`;
    }

    private disableCodeInputs(): void {
        const inputs = document.querySelectorAll<HTMLInputElement>('.code-digit');
        inputs.forEach(input => {
            input.disabled = true;
        });
    }

    private enableCodeInputs(): void {
        const inputs = document.querySelectorAll<HTMLInputElement>('.code-digit');
        inputs.forEach(input => {
            input.disabled = false;
        });
    }

    private showCodeSection(): void {
        if (this.codeSectionShown) return;
        this.codeSectionShown = true;
        this.show('login-code-section');

        // Focus the first code digit input
        const firstInput = document.querySelector<HTMLInputElement>('.code-digit');
        if (firstInput) {
            firstInput.focus();
        }
    }

    private startPolling(): void {
        if (!this.requestId) {
            return;
        }

        // Poll every 2 seconds
        this.pollingInterval = window.setInterval(() => {
            this.checkStatus();
        }, 2000);
    }

    private async checkStatus(): Promise<void> {
        if (!this.requestId) {
            this.stopPolling();
            return;
        }

        try {
            const status = await this.apiClient.checkMagicLinkStatus(this.requestId);

            // Show code input if link was opened on different device
            if (status.cross_device_detected) {
                this.showCodeSection();
            }

            if (status.invalidated) {
                this.stopPolling();

                // Show message that a newer request replaced this one
                this.showError(t('auth.signin_replaced'));

                // Hide the polling UI
                this.hide('login-polling');
            } else if (status.verified) {
                this.stopPolling();

                // Login completed in another tab - refresh token cookie is now set
                // Wait briefly for cookie to be fully available, then use it
                await this.completeLoginViaRefresh();
            }
        } catch (error) {
            // Silently fail polling errors - user can still use code input
            console.error('Polling error:', error);
        }
    }

    private stopPolling(): void {
        if (this.pollingInterval !== null) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    /**
     * Complete login by using the refresh token cookie set by the verify page
     * Called when polling detects that verification completed in another tab
     */
    private async completeLoginViaRefresh(): Promise<void> {
        // Brief delay to ensure cookie from verify response is fully set
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
            // Get access token via refresh (uses the cookie set by verify page)
            const token = await Auth.getValidAccessToken();

            if (!token) {
                // Refresh failed - show error and let user retry
                this.showError(t('auth.session_expired_request_new'));
                this.hide('login-polling');
                return;
            }

            // Ensure session hint is set for Safari tab restore workaround
            Auth.setSessionHint();

            // Show session persistence prompt
            this.hide('login-polling');
            this.showSessionPrompt();
        } catch (error) {
            console.error('Login completion error:', error);
            this.showError(t('auth.unexpected_error'));
            this.hide('login-polling');
        }
    }

    /**
     * Show the "Keep me signed in?" prompt after successful authentication
     * Replaces the login card content with the session persistence choice
     */
    private showSessionPrompt(): void {
        // Hide all login card content including the sign-in header
        this.hide('login-header');
        this.hide('login-form');
        this.hide('login-webauthn');
        this.hide('login-polling');
        this.hideError();
        this.hideSuccess();

        // Show the session prompt
        this.show('login-session-prompt');

        // Handle "Keep me signed in" button
        this.on('login-persist-yes', 'click', async () => {
            this.disable('login-persist-yes');
            this.disable('login-persist-no');

            try {
                const result = await this.apiClient.persistSession();
                // Update stored access token with the new one from persist
                this.storeAccessToken(result.access_token);
            } catch (error) {
                // If persist fails, continue with short session — user is still logged in
                console.error('Session persist failed:', error);
            }

            window.location.href = this.getRedirectUrl();
        });

        // Handle "Not now" button
        this.on('login-persist-no', 'click', () => {
            window.location.href = this.getRedirectUrl();
        });
    }

    private storeAccessToken(token: string): void {
        Auth.storeAccessToken(token);
    }

    private isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    private showError(message: string): void {
        this.setText('login-error', message);
        this.show('login-error');
    }

    private hideError(): void {
        this.hide('login-error');
    }

    private showSuccess(message: string): void {
        this.setText('login-success', message);
        this.show('login-success');
    }

    private hideSuccess(): void {
        this.hide('login-success');
    }

    /**
     * Cleanup when page is unloaded
     */
    destroy(): void {
        this.stopPolling();
        this.abortConditionalAuth();
    }
}

// Initialize the login page
const loginPage = new LoginPage();

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    loginPage.destroy();
});
